import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Breadcrumbs } from "../../../components/Breadcrumbs";
import type { BreadcrumbItem } from "../../../components/Breadcrumbs";
import { PageShell } from "../../shared/layouts/PageShell";
import { PageHeader } from "../../shared/ui/layouts/PageHeader";
import { useAuth } from "../../../providers/AuthProvider";
import { useToast } from "../../../providers/ToastProvider";
import { getLot, moveLot, type Lot } from "../api/lots";
import {
  listWorkspaces,
  type Workspace,
} from "../../workspaces/api/workspaces";
import { ArrowLeft, ArrowsLeftRight } from "@phosphor-icons/react";
import { defaultIconProps } from "../../shared/ui/icons";
import { ApiError } from "../../../services/apiClient";

const pageStackClasses = "flex flex-col gap-8 text-slate-900";
const cardClasses = "rounded-2xl border border-slate-200 bg-white p-6 shadow-sm flex flex-col gap-6";
const summaryClasses = "grid grid-cols-1 gap-4 sm:grid-cols-2 rounded-xl bg-slate-50 p-4 border border-slate-100";
const actionClasses = "flex items-center justify-end gap-3 pt-4 border-t border-slate-200";
const inputClasses =
  "block w-full rounded-xl border-0 py-2.5 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-sky-600 sm:text-sm sm:leading-6 disabled:bg-slate-50 disabled:text-slate-500 disabled:ring-slate-200";
const ghostButtonClasses = "pm-btn pm-btn-ghost";
const primaryButtonClasses = "pm-btn pm-btn-primary";

export function filterMovableWorkspaces(
  workspaces: Workspace[],
  currentWorkspaceId: string | undefined,
): Workspace[] {
  if (!currentWorkspaceId) {
    return [];
  }
  return workspaces.filter((workspace) => {
    return (
      workspace.id !== currentWorkspaceId &&
      workspace.role === "owner" &&
      !workspace.archived_at
    );
  });
}

export function LotMovePage() {
  const { t } = useTranslation();
  const { workspaceId, lotId } = useParams<{ workspaceId: string; lotId: string }>();
  const { accessToken } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const [lot, setLot] = useState<Lot | null>(null);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [selectedTargetId, setSelectedTargetId] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!accessToken || !lotId) {
      return;
    }
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [lotResponse, workspaceList] = await Promise.all([
          getLot(accessToken, lotId),
          listWorkspaces(accessToken),
        ]);
        if (cancelled) {
          return;
        }
        setLot(lotResponse);
        setWorkspaces(workspaceList);
      } catch (err) {
        if (cancelled) {
          return;
        }
        const message =
          err instanceof ApiError
            ? err.message
            : t("workspaces.lot_move.load_error");
        setError(message);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [accessToken, lotId, t]);

  const currentWorkspace = useMemo(() => {
    if (!workspaceId) {
      return undefined;
    }
    return workspaces.find((workspace) => workspace.id === workspaceId);
  }, [workspaceId, workspaces]);

  const targetOptions = useMemo(
    () => filterMovableWorkspaces(workspaces, lot?.workspace_id),
    [workspaces, lot?.workspace_id],
  );

  useEffect(() => {
    if (targetOptions.length > 0) {
      setSelectedTargetId((current) => current || targetOptions[0].id);
    } else {
      setSelectedTargetId("");
    }
  }, [targetOptions]);

  const breadcrumbItems = useMemo<BreadcrumbItem[]>(() => {
    const items: BreadcrumbItem[] = [
      { label: t("workspaces.my_workspaces.title"), to: "/workspaces" },
    ];
    if (currentWorkspace && workspaceId) {
      items.push({
        label: currentWorkspace.name,
        to: `/workspaces/${workspaceId}/lots`,
      });
    }
    if (lot) {
      items.push({ label: lot.name });
    }
    items.push({ label: t("workspaces.lot_move.title") });
    return items;
  }, [currentWorkspace, lot, t, workspaceId]);

  const targetWorkspace = useMemo(() => {
    return targetOptions.find((workspace) => workspace.id === selectedTargetId);
  }, [targetOptions, selectedTargetId]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!accessToken || !lotId || !selectedTargetId) {
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      const updated = await moveLot(accessToken, lotId, {
        target_workspace_id: selectedTargetId,
      });
      toast.success(
        t("workspaces.lot_move.success", {
          workspace: targetWorkspace?.name ?? "",
        }),
      );
      navigate(`/workspaces/${selectedTargetId}/lots/${lotId}/edit`, {
        state: { workspace: targetWorkspace },
      });
      setLot(updated);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : t("workspaces.lot_move.submit_error");
      setError(message);
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const pageTitle = t("workspaces.lot_move.title");
  const pageSubtitle = t("workspaces.lot_move.subtitle");

  const moveDisabled = !selectedTargetId || targetOptions.length === 0 || isSubmitting;

  if (isLoading) {
    return (
      <PageShell className={pageStackClasses} width="narrow">
        <Breadcrumbs items={breadcrumbItems} />
        <PageHeader
          title={pageTitle}
          subtitle={pageSubtitle}
        />
        <p className="text-center py-12 text-slate-500 italic">{t("workspaces.lot_move.loading")}</p>
      </PageShell>
    );
  }

  return (
    <PageShell className={pageStackClasses} width="wide">
      <Breadcrumbs items={breadcrumbItems} />
      <PageHeader
        title={pageTitle}
        subtitle={pageSubtitle}
        actions={
          <div className="flex items-center gap-3">
            {workspaceId && lotId ? (
              <Link
                className={ghostButtonClasses}
                to={`/workspaces/${workspaceId}/lots/${lotId}/edit`}
                state={location.state}
              >
                <ArrowLeft {...defaultIconProps} aria-hidden />
                {t("workspaces.lot_move.back_to_lot")}
              </Link>
            ) : null}
          </div>
        }
      />

      {error ? <p className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</p> : null}

      <form className={cardClasses} onSubmit={handleSubmit}>
        <section className="flex flex-col gap-6">
          <header className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-600">
              <ArrowsLeftRight {...defaultIconProps} aria-hidden />
            </div>
            <div>
              <p className="text-lg font-semibold text-slate-900">
                {t("workspaces.lot_move.summary_title")}
              </p>
              <p className="text-sm text-slate-500">
                {t("workspaces.lot_move.summary_subtitle")}
              </p>
            </div>
          </header>

          <dl className={summaryClasses}>
            <div className="flex flex-col gap-1">
              <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">{t("workspaces.lot_move.current_workspace_label")}</dt>
              <dd className="font-medium text-slate-900">{currentWorkspace?.name ?? "—"}</dd>
            </div>
            <div className="flex flex-col gap-1">
              <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">{t("workspaces.lot_move.lot_label")}</dt>
              <dd className="font-medium text-slate-900">{lot?.name ?? "—"}</dd>
            </div>
          </dl>

          <p className="text-sm text-slate-600">
            {t("workspaces.lot_move.helper_text")}
          </p>

          <label className="flex flex-col gap-2">
            <span className="text-sm font-medium text-slate-700">{t("workspaces.lot_move.target_workspace_label")}</span>
            <select
              value={selectedTargetId}
              onChange={(event) => setSelectedTargetId(event.target.value)}
              disabled={targetOptions.length === 0 || isSubmitting}
              className={inputClasses}
            >
              {targetOptions.map((workspace) => (
                <option key={workspace.id} value={workspace.id}>
                  {workspace.name}
                </option>
              ))}
            </select>
            {targetOptions.length === 0 ? (
              <small className="text-xs text-slate-500">
                {t("workspaces.lot_move.no_targets_hint")}
              </small>
            ) : null}
          </label>
        </section>

        <div className={actionClasses}>
          <Link
            className={ghostButtonClasses}
            to={workspaceId ? `/workspaces/${workspaceId}/lots/${lotId}/edit` : "/workspaces"}
            state={location.state}
          >
            {t("actions.cancel")}
          </Link>
          <button
            type="submit"
            className={primaryButtonClasses}
            disabled={moveDisabled}
          >
            {t("workspaces.lot_move.submit")}
          </button>
        </div>
      </form>
    </PageShell>
  );
}
